import { useRef, useState, DragEvent, ChangeEvent, useCallback } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { MAX_FILE_SIZE, MAX_FILES, MAX_TOTAL_FILES_SIZE } from '../../../../utils/constant';
import { Typography } from 'src/components/new/Typography';
import { Button } from 'src/components/new/Button';
import StyledFilesUpload from 'src/pages/create-notice/styled/document-page-styles/StyledFilesUpload';

interface AddDocumentInputProps {
  onAdd: (file: File) => Promise<void>;
  isLoading: boolean;
  currentDocumentCount: number;
  currentTotalSizeBytes: number;
}

const AddDocumentInput = ({ onAdd, isLoading, currentDocumentCount, currentTotalSizeBytes }: AddDocumentInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [snackSettings, setSnackSettings] = useState({ open: false, snackMessage: '' });

  const openErrorSnackBar = useCallback((message: string) => {
    setSnackSettings({ snackMessage: message, open: true });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackSettings({ open: false, snackMessage: '' });
  };

  const clearFileInputValue = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFiles = useCallback(
    async (files: FileList): Promise<void> => {
      const fileArray = Array.from(files);

      if (fileArray.length + currentDocumentCount > MAX_FILES) {
        openErrorSnackBar(`Cannot upload more than ${MAX_FILES} files!`);
        clearFileInputValue();
        return;
      }

      let runningTotalSize = currentTotalSizeBytes;

      for (const file of fileArray) {
        const isPdf =
          file.type === 'application/pdf' ||
          file.type === 'application/x-pdf' ||
          ((file.type === '' || file.type === 'application/octet-stream') &&
            file.name.toLowerCase().endsWith('.pdf'));

        if (!isPdf) {
          openErrorSnackBar('Only PDF files are supported.');
          continue;
        }

        if (file.size > MAX_FILE_SIZE) {
          openErrorSnackBar('File is too large, maximum size of 20 MB.');
          continue;
        }

        runningTotalSize += file.size;
        if (runningTotalSize > MAX_TOTAL_FILES_SIZE) {
          openErrorSnackBar('Total size of files exceeds maximum of 100 MB');
          break;
        }

        await onAdd(file);
      }

      clearFileInputValue();
    },
    [currentDocumentCount, currentTotalSizeBytes, onAdd, openErrorSnackBar, clearFileInputValue],
  );

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files) {
      handleFiles(event.target.files);
    }
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      await handleFiles(droppedFiles);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleWrapperClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isAtDocumentLimit = currentDocumentCount >= MAX_FILES;

  return (
    <Box sx={{ marginTop: '1rem' }}>
      <StyledFilesUpload
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={isAtDocumentLimit || isLoading ? undefined : handleWrapperClick}
        hasItems={true}
        style={{
          cursor: isAtDocumentLimit || isLoading ? 'not-allowed' : 'pointer',
          opacity: isAtDocumentLimit || isLoading ? 0.6 : 1,
          background: 'rgba(67, 110, 178, 0.05)',
        }}
      >
        <Button
          startIcon={<FileUploadOutlinedIcon sx={{ color: '#436EB2', fontSize: '1.125rem', marginRight: '0.25rem' }} />}
          type="primary"
          disabled={isAtDocumentLimit || isLoading}
          sx={{
            textTransform: 'initial',
            marginBottom: '0.625rem',
            width: '8.75rem',
            backgroundColor: '#E4E9F4',
            color: '#436EB2',
            border: '1px solid #436EB2',
            '&:hover': { backgroundColor: '#d6dff0' },
          }}
        >
          {isLoading ? 'Uploading...' : 'Select File'}
        </Button>
        <Typography sx={{ color: '#1D1B20', fontSize: '0.875rem' }}>
          or drop the .pdf file here.
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: '#757575', marginTop: '0.25rem' }}>
          Max File Size: 20 MB, Max Files: {MAX_FILES}, Max Total: 100 MB
        </Typography>
      </StyledFilesUpload>

      <input
        type="file"
        accept="application/pdf"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        multiple
      />

      <Snackbar
        open={snackSettings.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} variant="filled" severity="error" sx={{ width: '100%' }}>
          {snackSettings.snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddDocumentInput;
